import { useMemo } from "react";
import { HelpCircle } from "lucide-react";
import {
  getFieldRenderer,
  parseFieldProperties,
  parseFieldValidations,
} from "@/components/form-fields";
import type { FormWithFields } from "@/types/forms";
import type { FieldType } from "@/types/forms";

interface FormPreviewProps {
  form: FormWithFields;
}

export function FormPreview({ form }: FormPreviewProps) {
  const sortedFields = useMemo(
    () => [...form.fields].sort((a, b) => a.position - b.position),
    [form.fields]
  );

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-2">
          <h4 className="text-base font-semibold">{form.title}</h4>
          {form.description && (
            <span className="group relative">
              <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
              <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden w-48 rounded-md bg-popover border p-2 text-xs text-popover-foreground shadow-md group-hover:block">
                {form.description}
              </span>
            </span>
          )}
        </div>
        {form.required && (
          <p className="text-xs text-muted-foreground mt-1">Required form</p>
        )}
      </div>

      {sortedFields.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-4">
          Add fields to see a preview
        </p>
      ) : (
        sortedFields.map((field) => {
          const Renderer = getFieldRenderer(field.type as FieldType);
          return (
            <Renderer
              key={field.id}
              label={field.label}
              required={field.required}
              properties={parseFieldProperties(field.properties)}
              validations={parseFieldValidations(field.validations)}
              disabled
            />
          );
        })
      )}
    </div>
  );
}
